import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { PricingTierFormModal } from "./pricing-tier-form-modal";
import type { LenderPricingTier } from "@shared/schema";

interface PricingTiersPanelProps {
  lenderProductId: string;
}

export function PricingTiersPanel({ lenderProductId }: PricingTiersPanelProps) {
  const [showTierForm, setShowTierForm] = useState(false);
  const [selectedTier, setSelectedTier] = useState<LenderPricingTier | undefined>();
  const [deleteTierId, setDeleteTierId] = useState<string | null>(null);

  const { toast } = useToast();

  const { data: tiers = [], isLoading } = useQuery<LenderPricingTier[]>({
    queryKey: ["/api/crm/lender-products", lenderProductId, "pricing-tiers"],
  });

  // Delete pricing tier mutation
  const deleteTierMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/pricing-tiers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/lender-products", lenderProductId, "pricing-tiers"] });
      toast({
        title: "Pricing tier deleted",
        description: "The pricing tier has been deleted successfully.",
      });
      setDeleteTierId(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete pricing tier",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (tier: LenderPricingTier) => {
    setSelectedTier(tier);
    setShowTierForm(true);
  };

  const handleCloseModal = () => {
    setShowTierForm(false);
    setSelectedTier(undefined);
  };

  if (isLoading) {
    return <div className="p-4">Loading pricing tiers...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Pricing Tiers</h3>
        <Button
          size="sm"
          onClick={() => setShowTierForm(true)}
          data-testid="button-add-pricing-tier"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Tier
        </Button>
      </div>

      {tiers.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            No pricing tiers found. Click "Add Tier" to create one.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {tiers.map((tier) => (
            <Card
              key={tier.id}
              className="hover:bg-accent/50 transition-colors"
            >
              <CardHeader className="pb-2 pt-3 px-4">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-sm font-medium">
                    LVR {tier.lvrMin}% - {tier.lvrMax}%
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleEdit(tier)}
                      data-testid={`button-edit-tier-${tier.id}`}
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setDeleteTierId(tier.id)}
                      data-testid={`button-delete-tier-${tier.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pb-3 px-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Margin:</span>
                  <span className="font-medium">{(parseFloat(tier.margin) * 100).toFixed(2)}%</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PricingTierFormModal
        open={showTierForm}
        onOpenChange={handleCloseModal}
        lenderProductId={lenderProductId}
        pricingTier={selectedTier}
      />

      {/* Delete Pricing Tier Confirmation Dialog */}
      <Dialog open={!!deleteTierId} onOpenChange={(open) => !open && setDeleteTierId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Pricing Tier</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this pricing tier? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTierId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteTierId && deleteTierMutation.mutate(deleteTierId)}
              disabled={deleteTierMutation.isPending}
              data-testid="button-confirm-delete-tier"
            >
              {deleteTierMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
